import React, { useState, useEffect } from "react";

import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AutoAwesome, Check, Clear, Edit } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { useInsightMeta } from "@/context";

interface DescriptionCardProps {
  description: string;
  editable: boolean;
}

const DescriptionCard: React.FC<DescriptionCardProps> = ({ description, editable }) => {
  const theme = useTheme();
  const { updateDescription, saveDescription } = useInsightMeta();

  const [text, setText] = useState<string>("");
  const [draft, setDraft] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setText(description);
    setDraft(description);
  }, [description]);

  const handleEdit = () => {
    setDraft(text);
    setEditMode(true);
  };

  const handleCancel = () => {
    setDraft(text);
    setEditMode(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    await saveDescription(draft);
    setText(draft);
    setEditMode(false);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setLoading(true);
    const update = await updateDescription();
    setLoading(false);
    if (update) {
      setDraft(update);
      setEditMode(true);
    }
  };

  return (
    <Card
      sx={{
        border: 1,
        borderRadius: 1,
        borderColor: editMode
          ? theme.palette.primary.main
          : theme.palette.divider,
        maxHeight: "200px",
        position: "relative",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          pr: 3,
          "&:last-child": { pb: 2 },
          maxHeight: "200px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {editMode ? (
          <TextField
            variant="standard"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            multiline
            slotProps={{
              input: {
                disableUnderline: true,
                sx: {
                  typography: "body2",
                  padding: 0,
                },
              },
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 8,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {text}
          </Typography>
        )}
      </CardContent>

      {editable && (
        editMode ? (
          <HelpBox title="Cancel your changes">
            <IconButton
              onClick={handleCancel}
              sx={{
                p: 0.5,
                borderRadius: "6px",
                position: "absolute",
                right: 30,
                bottom: 2,
              }}
              disabled={loading}
            >
              <Clear fontSize="small" />
            </IconButton>
          </HelpBox>
        ) : (
          <HelpBox title="Regenerate a description">
            <IconButton
              onClick={handleRefresh}
              sx={{
                p: 0.5,
                borderRadius: "6px",
                position: "absolute",
                right: 30,
                bottom: 2,
                color: theme.palette.divider,
              }}
              disabled={loading}
            >
              <AutoAwesome fontSize="small" />
            </IconButton>
          </HelpBox>
        )
      )}

      {editable && (
        loading ? (
          <CircularProgress
            size="28px"
            sx={{
              p: 0.5,
              position: "absolute",
              right: 2,
              bottom: 2,
            }}
          />
        ) : (
          <HelpBox
            title={
              editMode
                ? "Save your changes"
                : "Edit the description of your audience"
            }
          >
            <IconButton
              onClick={editMode ? handleConfirm : handleEdit}
              sx={{
                p: 0.5,
                borderRadius: "6px",
                position: "absolute",
                right: 2,
                bottom: 2,
                color: editMode
                  ? theme.palette.primary.main
                  : theme.palette.divider,
              }}
              disabled={loading}
            >
              {editMode ? <Check fontSize="small" /> : <Edit fontSize="small" />}
            </IconButton>
          </HelpBox>
        )
      )}
    </Card>
  );
};

export default DescriptionCard;
